import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/GradientCard";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { tabAccessibleFor, moduleCanDo } from "../lib/modules";
import { toast } from "sonner";
import { Search, Building2, MapPin, ArrowUpRight, Plus, Eye, Edit3, KeyRound, Flame, UserPlus, FileText, FolderPlus, ExternalLink } from "lucide-react";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import DocStatusDialog, { PRE_ISSUES, STAGES, ATL2_STAGES } from "../components/DocStatusDialog";
import ClientFormDialog from "../components/ClientFormDialog";
import MapExistingDialog from "../components/MapExistingDialog";
import SchoolCredentialsDialog from "../components/SchoolCredentialsDialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const fmtDateTime = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
};

const STAGE_COLOR = {
  "Stage 1 — Drafting": "bg-violet-100 text-violet-700",
  "Stage 2 — Uploading": "bg-blue-100 text-blue-700",
  "Stage 3 — Review": "bg-amber-100 text-amber-700",
  "Stage 4 — Funded": "bg-emerald-100 text-emerald-700",
  "Stage 1 — Google Form": "bg-violet-100 text-violet-700",
  "Stage 2 — Portal Data": "bg-blue-100 text-blue-700",
  "Stage 3 — Application Status": "bg-amber-100 text-amber-700",
};

export default function Documentation() {
  const { user } = useAuth();
  const [viewMode] = useGlobalViewMode();
  const isMaster = user?.role === "master_admin";
  // v109.9.76 — role labels are informational only; permission logic uses
  // moduleCanDo (Universal Law: Master Access is the sole gate).
  const isCRMManager = user?.role === "crm_manager";
  const isSalesAdmin = user?.role === "sales_admin";
  const isDocAdmin = user?.role === "documentation_team";
  const canAssign = isMaster || moduleCanDo(user, "documentation", "edit");
  // Doc-tab access map — per-user grant of {unassigned, working, completed, inactive}.
  const [myDocTabAccess, setMyDocTabAccess] = useState({ unassigned: false, working: false, completed: false, inactive: false });
  // Tab visibility rule: Master Admin always; otherwise only the
  // explicit per-user grant unlocks each tab. Module-level access to
  // "documentation" ONLY unlocks the ATL 1.0 / ATL 2.0 tabs.
  const canViewUnassignedTab = isMaster || myDocTabAccess.unassigned;
  const canViewWorkingTab = isMaster || myDocTabAccess.working;
  const canViewCompletedTab = isMaster || myDocTabAccess.completed;
  const canViewInactiveTab = isMaster || myDocTabAccess.inactive;

  const [clients, setClients] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [workingDocs, setWorkingDocs] = useState([]);
  const [completedDocs, setCompletedDocs] = useState([]);
  const [inactiveDocs, setInactiveDocs] = useState([]);
  const [tab, setTab] = useState("atl1");

  // Snap back to ATL 1.0 if the active tab gets revoked at runtime
  // (e.g., admin removes a per-user grant while the page is open).
  useEffect(() => {
    if (tab === "unassigned" && !canViewUnassignedTab) setTab("atl1");
    else if (tab === "working" && !canViewWorkingTab) setTab("atl1");
    else if (tab === "completed" && !canViewCompletedTab) setTab("atl1");
    else if (tab === "inactive" && !canViewInactiveTab) setTab("atl1");
  }, [tab, canViewUnassignedTab, canViewWorkingTab, canViewCompletedTab, canViewInactiveTab]);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [lifecycleFilter, setLifecycleFilter] = useState("all");
  const [active, setActive] = useState({ client: null, mode: null }); // mode: "edit" | "view"
  const [credsClient, setCredsClient] = useState(null);
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [detailClient, setDetailClient] = useState(null);
  const [docEligible, setDocEligible] = useState([]);
  const [assignTarget, setAssignTarget] = useState(null); // client for which we're picking new doc-team
  // U-DISE inline edit
  const [udiseEditClient, setUdiseEditClient] = useState(null);
  const [udiseNewValue, setUdiseNewValue] = useState("");
  const [udiseSaving, setUdiseSaving] = useState(false);

  const openUdiseEdit = (c) => {
    setUdiseEditClient(c);
    setUdiseNewValue(c.u_dise || "");
  };
  const saveUdise = async () => {
    if (!udiseEditClient) return;
    const v = (udiseNewValue || "").trim();
    if (!v) { toast.error("Enter a U-DISE value"); return; }
    if (!/^\d{8,14}$/.test(v)) { toast.error("U-DISE must be 8–14 digits"); return; }
    if (v === (udiseEditClient.u_dise || "")) { setUdiseEditClient(null); return; }
    setUdiseSaving(true);
    try {
      const { data } = await api.patch(`/crm/documentation/${udiseEditClient.id}/update-udise`, { u_dise: v });
      toast.success(`U-DISE updated: ${data.previous_udise || "—"} → ${data.u_dise}`);
      setUdiseEditClient(null);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail || err.message)); }
    finally { setUdiseSaving(false); }
  };
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [assignSearch, setAssignSearch] = useState("");
  // v109.9.40 — Tab Access dialog removed; stub retained to avoid rippling changes.
  const tabAccessOpen = false; const setTabAccessOpen = () => {};
  void tabAccessOpen; void setTabAccessOpen;

  const loadDocEligible = async () => {
    if (!canAssign) return;
    try {
      // The Assign dialog now lists EVERY active employee — picking a user
      // also auto-grants them `documentation` module at Edit level (backend
      // side effect of POST /crm/clients/{id}/assign-doc-team).
      const { data } = await api.get("/documentation/assignable-users");
      setDocEligible(data || []);
    } catch (e) { /* silent */ }
  };

  // For non-master/non-doc-admin users: fetch their doc-tab grants so we can
  // optionally show extra tabs (Unassigned / Completed / Inactive).
  const loadMyDocTabAccess = async () => {
    if (isMaster || isDocAdmin) {
      setMyDocTabAccess({ unassigned: true, working: true, completed: true, inactive: true });
      return;
    }
    try {
      const probe = await Promise.allSettled([
        api.get("/crm/documentation-by-lifecycle?lifecycle=Working"),
        api.get("/crm/documentation-by-lifecycle?lifecycle=Completed"),
        api.get("/crm/documentation-by-lifecycle?lifecycle=Inactive"),
        api.get("/crm/unassigned-doc-clients"),
      ]);
      setMyDocTabAccess({
        working: probe[0].status === "fulfilled",
        completed: probe[1].status === "fulfilled",
        inactive: probe[2].status === "fulfilled",
        unassigned: probe[3].status === "fulfilled",
      });
    } catch (e) { /* silent */ }
  };

  const openAssign = (c) => {
    setAssignTarget(c);
    setSelectedAssignees(c.documentation_assignee_ids || []);
  };

  const toggleAssignee = (id) => {
    setSelectedAssignees((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  };

  const submitAssign = async () => {
    if (!assignTarget) return;
    try {
      await api.post(`/crm/clients/${assignTarget.id}/assign-doc-team`, { user_ids: selectedAssignees });
      toast.success("Doc team assigned ✓");
      setAssignTarget(null);
      setSelectedAssignees([]);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const setLifecycle = async (c, lifecycle) => {
    try {
      await api.patch(`/crm/clients/${c.id}/doc-lifecycle`, { lifecycle });
      toast.success(`Marked ${lifecycle}`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const load = async () => {
    try {
      const promises = [api.get("/crm/my-doc-clients")];
      if (canViewUnassignedTab) promises.push(api.get("/crm/unassigned-doc-clients")); else promises.push(Promise.resolve({ data: [] }));
      if (canViewWorkingTab) promises.push(api.get("/crm/documentation-by-lifecycle?lifecycle=Working")); else promises.push(Promise.resolve({ data: [] }));
      if (canViewCompletedTab) promises.push(api.get("/crm/documentation-by-lifecycle?lifecycle=Completed")); else promises.push(Promise.resolve({ data: [] }));
      if (canViewInactiveTab) promises.push(api.get("/crm/documentation-by-lifecycle?lifecycle=Inactive")); else promises.push(Promise.resolve({ data: [] }));
      const results = await Promise.all(promises);
      setClients(results[0].data || []);
      setUnassigned(results[1]?.data || []);
      setWorkingDocs(results[2]?.data || []);
      setCompletedDocs(results[3]?.data || []);
      setInactiveDocs(results[4]?.data || []);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // Drive folder per school — auto-spawned in the Documentation Shared Drive.
  // Folder name = "{ClientType} - {UDISE} - {SchoolName} - {SAI}".
  const [folderBusyId, setFolderBusyId] = useState("");
  const createDocFolder = async (c) => {
    if (!c?.id) return;
    setFolderBusyId(c.id);
    try {
      const { data } = await api.post(`/crm/documentation/${c.id}/drive-folder`);
      toast.success(`Drive folder ready · ${data.name || ""}`);
      // Optimistic in-place update across all client lists
      const patch = (list) => list.map((x) => x.id === c.id ? { ...x, doc_drive_folder_url: data.web_view_link, doc_drive_folder_id: data.folder_id, doc_drive_folder_name: data.name } : x);
      setClients((l) => patch(l));
      setUnassigned((l) => patch(l));
      setWorkingDocs((l) => patch(l));
      setCompletedDocs((l) => patch(l));
      setInactiveDocs((l) => patch(l));
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setFolderBusyId(""); }
  };

  useEffect(() => {
    loadMyDocTabAccess();
    loadDocEligible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, canViewUnassignedTab, canViewWorkingTab, canViewCompletedTab, canViewInactiveTab]);

  // My ATL tabs exclude Completed + Inactive clients — those flow to the
  // dedicated Completed / Inactive tabs (Master / Doc Admin / explicit tab access).
  const myActiveClients = useMemo(() => clients.filter((c) => {
    const lc = c.doc_lifecycle || "";
    return lc !== "Completed" && lc !== "Inactive";
  }), [clients]);
  const atl1Clients = useMemo(() => myActiveClients.filter((c) => (c.client_type === "ATL 1.0") || (c.client_types || []).includes("ATL 1.0")), [myActiveClients]);
  const atl2Clients = useMemo(() => myActiveClients.filter((c) => (c.client_type === "ATL 2.0") || (c.client_types || []).includes("ATL 2.0")), [myActiveClients]);

  // Completed / Inactive / Working split per ATL type (filter chips inside each parent tab)
  const workingATL1 = useMemo(() => workingDocs.filter((c) => (c.client_type === "ATL 1.0") || (c.client_types || []).includes("ATL 1.0")), [workingDocs]);
  const workingATL2 = useMemo(() => workingDocs.filter((c) => (c.client_type === "ATL 2.0") || (c.client_types || []).includes("ATL 2.0")), [workingDocs]);
  const completedATL1 = useMemo(() => completedDocs.filter((c) => (c.client_type === "ATL 1.0") || (c.client_types || []).includes("ATL 1.0")), [completedDocs]);
  const completedATL2 = useMemo(() => completedDocs.filter((c) => (c.client_type === "ATL 2.0") || (c.client_types || []).includes("ATL 2.0")), [completedDocs]);
  const inactiveATL1 = useMemo(() => inactiveDocs.filter((c) => (c.client_type === "ATL 1.0") || (c.client_types || []).includes("ATL 1.0")), [inactiveDocs]);
  const inactiveATL2 = useMemo(() => inactiveDocs.filter((c) => (c.client_type === "ATL 2.0") || (c.client_types || []).includes("ATL 2.0")), [inactiveDocs]);
  const [workingSub, setWorkingSub] = useState("all");
  const [completedSub, setCompletedSub] = useState("all");
  const [inactiveSub, setInactiveSub] = useState("all");
  const [logsClient, setLogsClient] = useState(null);

  // Choose the appropriate stage list per active tab (Documentation Support
  // is gated to ATL 1.0 / ATL 2.0 only — no "Other" tab anymore).
  const stagesForTab = useMemo(() => {
    if (tab === "atl1") return STAGES;
    if (tab === "atl2") return ATL2_STAGES;
    return Array.from(new Set([...STAGES, ...ATL2_STAGES]));
  }, [tab]);

  // Reset stage filter whenever the tab changes so we don't carry over a filter that
  // doesn't apply to the new tab.
  useEffect(() => { setStageFilter("all"); }, [tab]);

  const applyFilters = (list) => {
    const q = search.toLowerCase();
    let out = list;
    if (stageFilter !== "all") {
      out = out.filter((c) => (c.documentation_status?.current_stage || c.documentation_status?.atl2_current_stage || "") === stageFilter);
    }
    if (lifecycleFilter !== "all") {
      if (lifecycleFilter === "Pending") {
        out = out.filter((c) => !c.doc_lifecycle);
      } else {
        out = out.filter((c) => (c.doc_lifecycle || "") === lifecycleFilter);
      }
    }
    if (q) {
      out = out.filter((c) => [c.client_name, c.u_dise, c.district, c.state, c.client_type, c.assigned_to_name].some((v) => String(v ?? "").toLowerCase().includes(q)));
    }
    return out;
  };

  const filtered = useMemo(() => {
    if (tab === "atl1") return applyFilters(atl1Clients);
    if (tab === "atl2") return applyFilters(atl2Clients);
    return applyFilters(clients);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, atl1Clients, atl2Clients, search, stageFilter, lifecycleFilter, tab]);

  const [unassignedATLFilter, setUnassignedATLFilter] = useState("all"); // "all" | "atl1" | "atl2"

  const filteredUnassigned = useMemo(() => {
    const q = search.toLowerCase();
    let list = unassigned;
    if (unassignedATLFilter === "atl1") {
      list = list.filter((c) => (c.client_type === "ATL 1.0") || (c.client_types || []).includes("ATL 1.0"));
    } else if (unassignedATLFilter === "atl2") {
      list = list.filter((c) => (c.client_type === "ATL 2.0") || (c.client_types || []).includes("ATL 2.0"));
    }
    if (q) {
      list = list.filter((c) => [c.client_name, c.u_dise, c.district, c.state, c.client_type, c.assigned_to_name].some((v) => String(v ?? "").toLowerCase().includes(q)));
    }
    return list;
  }, [unassigned, search, unassignedATLFilter]);

  // Stats — per-stage counts use only the active tab's relevant stages
  const stageStats = useMemo(() => {
    const counts = {};
    const bucket = tab === "atl1" ? atl1Clients : tab === "atl2" ? atl2Clients : clients;
    bucket.forEach((c) => {
      const k = c.documentation_status?.current_stage || c.documentation_status?.atl2_current_stage || "Unspecified";
      counts[k] = (counts[k] || 0) + 1;
    });
    return counts;
  }, [clients, atl1Clients, atl2Clients, tab]);

  // Lifecycle counts for current tab's bucket — only Working / Pending
  // matter on ATL tabs because Completed / Inactive get their own tabs.
  const lifecycleStats = useMemo(() => {
    const counts = { Working: 0, Pending: 0, Completed: 0, Inactive: 0, Unassigned: 0 };
    const bucket = tab === "unassigned" ? unassigned : tab === "atl1" ? atl1Clients : tab === "atl2" ? atl2Clients : clients;
    bucket.forEach((c) => {
      const lc = c.doc_lifecycle || "Pending";
      counts[lc] = (counts[lc] || 0) + 1;
    });
    return counts;
  }, [clients, unassigned, atl1Clients, atl2Clients, tab]);

  // NOTE: The previous "Claim" workflow has been REMOVED. Doc clients now
  // flow only via explicit assignment by Master Admin / Documentation Admin
  // through the Unassigned tab → "Assign" dialog. No self-claim path.

  return (
    <div className="max-w-[1500px] space-y-6">
      <PageHeader
        moduleKey="documentation"
        tone="violet"
        icon={FileText}
        eyebrow="Sales · Documentation Workflow"
        title="Documentation"
        subtitle="Schools flagged for post-sales documentation. Track UC drafting, uploads, school approval and fund credit stage by stage."
        actions={
          <>
            <Button variant="outline" onClick={() => setMapOpen(true)} className="h-10" data-testid="doc-map-existing">
              <ArrowUpRight size={14} className="mr-1.5 rotate-180" /> Map a Client via U-DISE
            </Button>
            <Button onClick={() => setNewClientOpen(true)} className="h-10" data-testid="doc-new-client">
              <Plus size={16} className="mr-1.5" /> Add New Client
            </Button>
          </>
        }
      />

      {/* Stats strip — uses current tab's stage list (ATL 1.0 vs ATL 2.0) */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="doc-stats-strip">
        <StatCard label="My Clients" value={clients.length} tone="violet" />
        <StatCard label="Unassigned" value={unassigned.length} tone="orange" />
        {stagesForTab.map((s) => (
          <StatCard key={s} label={s.replace(" — ", " · ")} value={stageStats[s] || 0} tone={tonefor(s)} />
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {tabAccessibleFor(user, "documentation", "atl1") && (
            <TabsTrigger value="atl1" data-testid="doc-tab-atl1">ATL 1.0 ({atl1Clients.length})</TabsTrigger>
          )}
          {tabAccessibleFor(user, "documentation", "atl2") && (
            <TabsTrigger value="atl2" data-testid="doc-tab-atl2">ATL 2.0 ({atl2Clients.length})</TabsTrigger>
          )}
          {canViewUnassignedTab && tabAccessibleFor(user, "documentation", "unassigned") && (
            <TabsTrigger value="unassigned" data-testid="doc-tab-unassigned">Unassigned ({unassigned.length})</TabsTrigger>
          )}
          {canViewWorkingTab && tabAccessibleFor(user, "documentation", "working") && (
            <TabsTrigger value="working" data-testid="doc-tab-working">Working ({workingDocs.length})</TabsTrigger>
          )}
          {canViewCompletedTab && tabAccessibleFor(user, "documentation", "completed") && (
            <TabsTrigger value="completed" data-testid="doc-tab-completed">Completed ({completedDocs.length})</TabsTrigger>
          )}
          {canViewInactiveTab && tabAccessibleFor(user, "documentation", "inactive") && (
            <TabsTrigger value="inactive" data-testid="doc-tab-inactive">Inactive ({inactiveDocs.length})</TabsTrigger>
          )}
        </TabsList>

        {tab !== "unassigned" && tab !== "completed" && tab !== "inactive" && tab !== "working" && (
        <div className="mt-4 space-y-4">
          {/* Filter row — search + stage chips (tab-specific) */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative max-w-sm flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients, U-DISE, sales owner…" className="pl-9 h-10" data-testid="doc-search-clients" />
            </div>
            {["all", ...stagesForTab].map((s) => (
              <button
                key={s}
                onClick={() => setStageFilter(s)}
                className={`h-9 px-3 text-[11.5px] font-semibold rounded-full border-2 transition-all ${stageFilter === s ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-violet-200 text-violet-700 hover:bg-violet-50"}`}
                data-testid={`doc-stage-chip-${s.replace(/[^A-Za-z0-9]/g, "_")}`}
              >
                {s === "all" ? `All Stages` : `${s.replace(" — ", " · ")} (${stageStats[s] || 0})`}
              </button>
            ))}
          </div>

          {/* Status filter — ATL 1.0 / ATL 2.0 only carry Working + pending
              (no-status-yet) clients; Completed/Inactive get their own tabs.
              Colours mirror the requested global convention: Working=Blue,
              Completed=Green, Inactive=Red. */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Status</span>
            {["all", "Working", "Pending"].map((lc) => (
              <button
                key={lc}
                onClick={() => setLifecycleFilter(lc)}
                className={`h-8 px-3 text-[11.5px] font-semibold rounded-full border-2 transition-all ${lifecycleFilter === lc ? lifecycleChipActive(lc) : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`}
                data-testid={`doc-lifecycle-chip-${lc}`}
              >
                {lc === "all" ? "All" : `${lc} (${lifecycleStats[lc] || 0})`}
              </button>
            ))}
          </div>

          <div className={viewMode === "card" ? "hidden" : "pk-card overflow-x-auto"}>
            <table className="w-full text-sm responsive-table">
              <thead className="border-b border-slate-200 bg-slate-50/60">
                <tr>
                  <th className="px-4 py-3 text-left pk-table-header">Client / School</th>
                  <th className="px-4 py-3 text-left pk-table-header">U-DISE</th>
                  <th className="px-4 py-3 text-left pk-table-header">Sales Owner</th>
                  <th className="px-4 py-3 text-left pk-table-header">Current Stage</th>
                  <th className="px-4 py-3 text-left pk-table-header">Doc Team</th>
                  <th className="px-4 py-3 text-left pk-table-header">Drive Folder</th>
                  <th className="px-4 py-3 text-left pk-table-header">Previous Issues</th>
                  <th className="px-4 py-3 text-left pk-table-header">Last Update</th>
                  <th className="px-4 py-3 text-right pk-table-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-12 text-slate-400 text-sm">No documentation clients in this view.</td></tr>
                )}
                {filtered.map((c) => {
                  const isATL2 = (c.client_type === "ATL 2.0") || (c.client_types || []).includes("ATL 2.0");
                  const stage = isATL2
                    ? (c.documentation_status?.atl2_current_stage || "")
                    : (c.documentation_status?.current_stage || "");
                  const pre = c.documentation_status?.pre_issues || [];
                  const lifecycle = c.doc_lifecycle || "";
                  const lifecycleColor = lifecycle === "Completed" ? "bg-emerald-100 text-emerald-700" : lifecycle === "Inactive" ? "bg-slate-200 text-slate-700" : "bg-blue-100 text-blue-700";
                  return (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`doc-client-${c.u_dise || c.id}`}>
                      <td className="px-4 py-3" data-label="Client">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-md bg-violet-700 text-white grid place-items-center shrink-0"><Building2 size={14} strokeWidth={1.5} /></div>
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5 flex-wrap">
                              {c.client_name}
                              {c.is_hot && <span className="inline-flex items-center gap-0.5 bg-rose-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full"><Flame size={9} /> HOT</span>}
                              {isATL2 && <span className="bg-pink-100 text-pink-700 text-[9px] font-bold px-1.5 py-0.5 rounded-full">ATL 2.0</span>}
                            </div>
                            {(c.district || c.state) && <div className="text-[10px] text-slate-500 flex items-center gap-1"><MapPin size={9} /> {[c.district, c.state].filter(Boolean).join(" · ")}</div>}
                            {lifecycle && (
                              <Badge className={`${lifecycleColor} text-[10px] mt-1 hover:opacity-90`}>{lifecycle}</Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 mono text-[12px]" data-label="U-DISE">
                        <div className="inline-flex items-center gap-1.5">
                          <span>{c.u_dise || "—"}</span>
                          <button
                            type="button"
                            onClick={() => openUdiseEdit(c)}
                            title="Edit U-DISE (updates Client Database)"
                            className="text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded p-0.5 transition-colors"
                            data-testid={`doc-edit-udise-${c.id}`}
                          >
                            <Edit3 size={12} />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[12px]" data-label="Sales Owner">{c.assigned_to_name || <span className="text-slate-400 italic">— Unassigned</span>}</td>
                      <td className="px-4 py-3" data-label="Current Stage">
                        {stage ? <Badge className={`${STAGE_COLOR[stage] || "bg-slate-100 text-slate-700"} text-[10px] hover:opacity-90`}>{stage}</Badge> : <span className="text-slate-400 text-xs">— Not started</span>}
                        {isATL2 && c.documentation_status?.atl2_application_status && (
                          <div className="text-[10px] mt-1">
                            <span className={
                              c.documentation_status.atl2_application_status === "Approved" ? "text-emerald-700 font-semibold" :
                              c.documentation_status.atl2_application_status === "Rejected" ? "text-rose-700 font-semibold" :
                              "text-amber-700"
                            }>
                              App: {c.documentation_status.atl2_application_status}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[12px]" data-label="Doc Team">
                        {(c.documentation_assignee_names || []).length === 0 ? (
                          <span className="text-amber-700 text-[11px] italic">— Unassigned</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {(c.documentation_assignee_names || []).map((n, i) => (
                              <span key={i} className="bg-violet-100 text-violet-800 text-[10px] px-1.5 py-0.5 rounded-full font-medium">{n}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3" data-label="Drive Folder">
                        {c.doc_drive_folder_url ? (
                          <a
                            href={c.doc_drive_folder_url}
                            target="_blank"
                            rel="noreferrer"
                            title={c.doc_drive_folder_name || "Open Drive folder"}
                            className="inline-flex items-center gap-1 text-emerald-700 hover:underline text-[11.5px] font-semibold"
                            data-testid={`doc-drive-link-${c.u_dise || c.id}`}
                          >
                            <ExternalLink size={11} /> Open
                          </a>
                        ) : (
                          <Button
                            onClick={() => createDocFolder(c)}
                            size="sm"
                            variant="outline"
                            disabled={folderBusyId === c.id}
                            className="h-7 px-2 text-[10.5px] bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                            data-testid={`doc-drive-create-${c.u_dise || c.id}`}
                          >
                            <FolderPlus size={10} className="mr-1" />
                            {folderBusyId === c.id ? "Creating…" : "Create"}
                          </Button>
                        )}
                      </td>
                      <td className="px-4 py-3" data-label="Previous Issues">
                        {pre.length === 0 ? <span className="text-slate-400 text-xs">—</span> : (
                          <div className="flex flex-wrap gap-1 max-w-[260px]">
                            {pre.slice(0, 2).map((p, i) => <span key={i} className="text-[10px] bg-orange-100 text-orange-800 rounded px-1.5 py-0.5">{p}</span>)}
                            {pre.length > 2 && <span className="text-[10px] text-slate-500">+{pre.length - 2}</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[11px] mono text-slate-600" data-label="Last Update">{fmtDateTime(c.documentation_status?.updated_at)}</td>
                      <td className="px-4 py-3 text-right" data-label="Actions">
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          <Button onClick={() => setDetailClient(c)} size="sm" variant="outline" className="h-8 px-2.5 text-[11px]" data-testid={`doc-details-${c.u_dise || c.id}`}>
                            <Eye size={11} className="mr-1" /> Details
                          </Button>
                          {canAssign && (
                            <Button onClick={() => openAssign(c)} size="sm" variant="outline" className="h-8 px-2.5 text-[11px] bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100" data-testid={`doc-assign-team-${c.u_dise || c.id}`}>
                              <UserPlus size={11} className="mr-1" /> Assign
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="outline" className={`h-8 px-2.5 text-[11px] ${lifecycle === "Working" ? "bg-blue-50 border-blue-300 text-blue-800" : lifecycle === "Completed" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : lifecycle === "Inactive" ? "bg-slate-100 border-slate-300 text-slate-700" : ""}`} data-testid={`doc-lifecycle-${c.u_dise || c.id}`}>
                                {lifecycle || "Mark Status"} ▾
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {["Working", "Completed", "Inactive"].map((s) => (
                                <DropdownMenuItem key={s} onClick={() => setLifecycle(c, s)} className="text-xs cursor-pointer" data-testid={`doc-lifecycle-${s}-${c.u_dise || c.id}`}>
                                  {s}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button onClick={() => setCredsClient(c)} size="sm" variant="outline" className="h-8 px-2.5 text-[11px]" data-testid={`doc-creds-${c.u_dise || c.id}`}>
                            <KeyRound size={11} className="mr-1" /> Creds
                          </Button>
                          <Button onClick={() => setActive({ client: c, mode: "view" })} size="sm" variant="outline" className="h-8 px-2.5 text-[11px]" data-testid={`doc-view-${c.u_dise || c.id}`}>
                            <Eye size={11} className="mr-1" /> View
                          </Button>
                          <Button onClick={() => setActive({ client: c, mode: "edit" })} size="sm" className="h-8 px-2.5 text-[11px] bg-violet-600 hover:bg-violet-700 text-white" data-testid={`doc-update-${c.u_dise || c.id}`}>
                            <Edit3 size={11} className="mr-1" /> Update
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {viewMode === "card" && (
            filtered.length === 0 ? (
              <div className="pk-card p-12 text-center text-slate-400 text-sm">No documentation clients in this view.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="doc-clients-cards">
                {filtered.map((c) => {
                  const isATL2 = (c.client_type === "ATL 2.0") || (c.client_types || []).includes("ATL 2.0");
                  const stage = isATL2
                    ? (c.documentation_status?.atl2_current_stage || "")
                    : (c.documentation_status?.current_stage || "");
                  const pre = c.documentation_status?.pre_issues || [];
                  const lifecycle = c.doc_lifecycle || "";
                  const lifecycleColor = lifecycle === "Completed" ? "bg-emerald-100 text-emerald-700" : lifecycle === "Inactive" ? "bg-slate-200 text-slate-700" : "bg-blue-100 text-blue-700";
                  return (
                    <div key={c.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`doc-client-card-${c.u_dise || c.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className="w-8 h-8 rounded-md bg-violet-700 text-white grid place-items-center shrink-0"><Building2 size={14} strokeWidth={1.5} /></div>
                          <div className="min-w-0">
                            <div className="font-bold text-slate-900 dark:text-slate-100 truncate flex items-center gap-1 flex-wrap">
                              {c.client_name}
                              {c.is_hot && <span className="inline-flex items-center gap-0.5 bg-rose-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full"><Flame size={9} /> HOT</span>}
                              {isATL2 && <span className="bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300 text-[9px] font-bold px-1.5 py-0.5 rounded-full">ATL 2.0</span>}
                            </div>
                            {(c.district || c.state) && <div className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center gap-1 truncate"><MapPin size={9} /> {[c.district, c.state].filter(Boolean).join(" · ")}</div>}
                          </div>
                        </div>
                        {lifecycle && <Badge className={`${lifecycleColor} text-[10px] shrink-0`}>{lifecycle}</Badge>}
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                        <div className="col-span-2">
                          <div className="text-[9.5px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">U-DISE</div>
                          <div className="mono text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                            <span>{c.u_dise || "—"}</span>
                            <button type="button" onClick={() => openUdiseEdit(c)} title="Edit U-DISE" className="text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded p-0.5 transition-colors" data-testid={`doc-edit-udise-card-${c.id}`}>
                              <Edit3 size={11} />
                            </button>
                          </div>
                        </div>
                        <div>
                          <div className="text-[9.5px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">Sales Owner</div>
                          <div className="text-slate-800 dark:text-slate-200 truncate">{c.assigned_to_name || <span className="text-slate-400 italic">— Unassigned</span>}</div>
                        </div>
                        <div>
                          <div className="text-[9.5px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">Current Stage</div>
                          {stage ? <Badge className={`${STAGE_COLOR[stage] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"} text-[10px]`}>{stage}</Badge> : <span className="text-slate-400 text-[10px]">— Not started</span>}
                          {isATL2 && c.documentation_status?.atl2_application_status && (
                            <div className="text-[10px] mt-1">
                              <span className={c.documentation_status.atl2_application_status === "Approved" ? "text-emerald-700 dark:text-emerald-400 font-semibold" : c.documentation_status.atl2_application_status === "Rejected" ? "text-rose-700 dark:text-rose-400 font-semibold" : "text-amber-700 dark:text-amber-400"}>
                                App: {c.documentation_status.atl2_application_status}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="col-span-2">
                          <div className="text-[9.5px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">Doc Team</div>
                          {(c.documentation_assignee_names || []).length === 0 ? (
                            <span className="text-amber-700 dark:text-amber-400 text-[11px] italic">— Unassigned</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(c.documentation_assignee_names || []).map((n, i) => (
                                <span key={i} className="bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300 text-[10px] px-1.5 py-0.5 rounded-full font-medium">{n}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="col-span-2">
                          <div className="text-[9.5px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">Drive Folder</div>
                          {c.doc_drive_folder_url ? (
                            <a href={c.doc_drive_folder_url} target="_blank" rel="noreferrer" title={c.doc_drive_folder_name || "Open Drive folder"} className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 hover:underline text-[11.5px] font-semibold">
                              <ExternalLink size={11} /> {c.doc_drive_folder_name || "Open"}
                            </a>
                          ) : (
                            <Button onClick={() => createDocFolder(c)} size="sm" variant="outline" className="h-6 px-2 text-[10.5px] text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/30" disabled={folderBusyId === c.id} data-testid={`doc-create-folder-card-${c.id}`}>
                              <FolderPlus size={10} className="mr-1" />
                              {folderBusyId === c.id ? "Creating…" : "Create"}
                            </Button>
                          )}
                        </div>
                        <div className="col-span-2">
                          <div className="text-[9.5px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">Previous Issues</div>
                          {pre.length === 0 ? <span className="text-slate-400 text-[10.5px]">—</span> : (
                            <div className="flex flex-wrap gap-1">
                              {pre.map((p, i) => <span key={i} className="text-[10px] bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 rounded px-1.5 py-0.5">{p}</span>)}
                            </div>
                          )}
                        </div>
                        <div className="col-span-2">
                          <div className="text-[9.5px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">Last Update</div>
                          <div className="mono text-[10.5px] text-slate-600 dark:text-slate-300">{fmtDateTime(c.documentation_status?.updated_at)}</div>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex flex-wrap gap-1.5">
                        <Button onClick={() => setDetailClient(c)} size="sm" variant="outline" className="h-7 px-2 text-[10.5px]" data-testid={`doc-details-card-${c.u_dise || c.id}`}>
                          <Eye size={10} className="mr-1" /> Details
                        </Button>
                        {canAssign && (
                          <Button onClick={() => openAssign(c)} size="sm" variant="outline" className="h-7 px-2 text-[10.5px] bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100" data-testid={`doc-assign-team-card-${c.u_dise || c.id}`}>
                            <UserPlus size={10} className="mr-1" /> Assign
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" className={`h-7 px-2 text-[10.5px] ${lifecycle === "Working" ? "bg-blue-50 border-blue-300 text-blue-800" : lifecycle === "Completed" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : lifecycle === "Inactive" ? "bg-slate-100 border-slate-300 text-slate-700" : ""}`} data-testid={`doc-lifecycle-card-${c.u_dise || c.id}`}>
                              {lifecycle || "Mark Status"} ▾
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {["Working", "Completed", "Inactive"].map((s) => (
                              <DropdownMenuItem key={s} onClick={() => setLifecycle(c, s)} className="text-xs cursor-pointer" data-testid={`doc-lifecycle-card-${s}-${c.u_dise || c.id}`}>
                                {s}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Button onClick={() => setCredsClient(c)} size="sm" variant="outline" className="h-7 px-2 text-[10.5px]" data-testid={`doc-creds-card-${c.u_dise || c.id}`}>
                          <KeyRound size={10} className="mr-1" /> Creds
                        </Button>
                        <Button onClick={() => setActive({ client: c, mode: "view" })} size="sm" variant="outline" className="h-7 px-2 text-[10.5px]" data-testid={`doc-view-card-${c.u_dise || c.id}`}>
                          <Eye size={10} className="mr-1" /> View
                        </Button>
                        <Button onClick={() => setActive({ client: c, mode: "edit" })} size="sm" className="h-7 px-2 text-[10.5px] bg-violet-600 hover:bg-violet-700 text-white" data-testid={`doc-update-card-${c.u_dise || c.id}`}>
                          <Edit3 size={10} className="mr-1" /> Update
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
        )}

        {canViewUnassignedTab && (
        <TabsContent value="unassigned" className="mt-4 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative max-w-sm flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search unassigned doc clients…" className="pl-9 h-10" data-testid="doc-search-unassigned" />
            </div>
          </div>
          {/* Filter — ATL 1.0 / ATL 2.0 */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Filter</span>
            {[
              { key: "all", label: "All" },
              { key: "atl1", label: "ATL 1.0" },
              { key: "atl2", label: "ATL 2.0" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setUnassignedATLFilter(f.key)}
                className={`h-8 px-3 text-[11.5px] font-semibold rounded-full border transition-all ${unassignedATLFilter === f.key ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`}
                data-testid={`doc-unassigned-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className={viewMode === "card" ? "hidden" : "pk-card overflow-x-auto"}>
            <table className="w-full text-sm responsive-table">
              <thead className="border-b border-slate-200 bg-slate-50/60">
                <tr>
                  <th className="px-4 py-3 text-left pk-table-header">Client / School</th>
                  <th className="px-4 py-3 text-left pk-table-header">U-DISE</th>
                  <th className="px-4 py-3 text-left pk-table-header">Type</th>
                  <th className="px-4 py-3 text-left pk-table-header">Sales Owner</th>
                  <th className="px-4 py-3 text-right pk-table-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUnassigned.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-slate-400 text-sm">No unassigned doc clients right now.</td></tr>}
                {filteredUnassigned.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`doc-unassigned-${c.u_dise || c.id}`}>
                    <td className="px-4 py-3 font-semibold text-slate-900" data-label="Client">{c.client_name}</td>
                    <td className="px-4 py-3 mono text-[12px]" data-label="U-DISE">
                      <div className="inline-flex items-center gap-1.5">
                        <span>{c.u_dise || "—"}</span>
                        <button
                          type="button"
                          onClick={() => openUdiseEdit(c)}
                          title="Edit U-DISE (updates Client Database)"
                          className="text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded p-0.5 transition-colors"
                          data-testid={`doc-unassigned-edit-udise-${c.id}`}
                        >
                          <Edit3 size={12} />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-label="Type"><Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px]">{c.client_type || "—"}</Badge></td>
                    <td className="px-4 py-3 text-[12px]" data-label="Sales Owner">{c.assigned_to_name || <span className="text-slate-400 italic">— Unassigned</span>}</td>
                    <td className="px-4 py-3 text-right" data-label="Actions">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        <Button onClick={() => setDetailClient(c)} size="sm" variant="outline" className="h-8 px-2.5 text-[11px]" data-testid={`doc-unassigned-details-${c.u_dise || c.id}`}>
                          <Eye size={11} className="mr-1" /> Details
                        </Button>
                        {canAssign && (
                          <Button onClick={() => openAssign(c)} size="sm" variant="outline" className="h-8 px-2.5 text-[11px] bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100" data-testid={`doc-unassigned-assign-${c.u_dise || c.id}`}>
                            <UserPlus size={11} className="mr-1" /> Assign
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {viewMode === "card" && (
            filteredUnassigned.length === 0 ? (
              <div className="pk-card p-12 text-center text-slate-400 text-sm">No unassigned doc clients right now.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="doc-unassigned-cards">
                {filteredUnassigned.map((c) => (
                  <div key={c.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`doc-unassigned-card-${c.u_dise || c.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-900 dark:text-slate-100 truncate">{c.client_name}</div>
                        <div className="mono text-[10.5px] text-slate-500 dark:text-slate-400 truncate flex items-center gap-1.5">
                          <span>U-DISE {c.u_dise || "—"}</span>
                          <button
                            type="button"
                            onClick={() => openUdiseEdit(c)}
                            title="Edit U-DISE"
                            className="text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded p-0.5 transition-colors"
                            data-testid={`doc-unassigned-edit-udise-card-${c.id}`}
                          >
                            <Edit3 size={10} />
                          </button>
                        </div>
                      </div>
                      <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 hover:bg-indigo-100 text-[10px] shrink-0">{c.client_type || "—"}</Badge>
                    </div>
                    <div className="text-[11.5px] mb-2">
                      <div className="text-[9.5px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">Sales Owner</div>
                      <div className="text-slate-800 dark:text-slate-200 truncate">{c.assigned_to_name || <span className="text-slate-400 italic">— Unassigned</span>}</div>
                    </div>
                    <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex flex-wrap gap-1.5">
                      <Button onClick={() => setDetailClient(c)} size="sm" variant="outline" className="h-7 px-2 text-[10.5px]" data-testid={`doc-unassigned-details-card-${c.u_dise || c.id}`}>
                        <Eye size={10} className="mr-1" /> Details
                      </Button>
                      {canAssign && (
                        <Button onClick={() => openAssign(c)} size="sm" variant="outline" className="h-7 px-2 text-[10.5px] bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100" data-testid={`doc-unassigned-assign-card-${c.u_dise || c.id}`}>
                          <UserPlus size={10} className="mr-1" /> Assign
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </TabsContent>
        )}

        {canViewWorkingTab && (
          <TabsContent value="working" className="mt-4 space-y-3" data-testid="doc-working-tab">
            <LifecycleSubTabs
              tone="amber"
              label="Working"
              currentLifecycle="Working"
              sub={workingSub}
              setSub={setWorkingSub}
              atl1List={workingATL1}
              atl2List={workingATL2}
              onView={(c) => setDetailClient(c)}
              onCreds={isMaster ? (c) => setCredsClient(c) : null}
              onAssign={canAssign ? (c) => openAssign(c) : null}
              onViewLogs={(c) => setLogsClient(c)}
              onChangeStatus={setLifecycle}
              onCreateFolder={createDocFolder}
              folderBusyId={folderBusyId}
              onEditUdise={openUdiseEdit}
            />
          </TabsContent>
        )}

        {canViewCompletedTab && (
          <TabsContent value="completed" className="mt-4 space-y-3" data-testid="doc-completed-tab">
            <LifecycleSubTabs
              tone="emerald"
              label="Completed"
              currentLifecycle="Completed"
              sub={completedSub}
              setSub={setCompletedSub}
              atl1List={completedATL1}
              atl2List={completedATL2}
              onView={(c) => setDetailClient(c)}
              onCreds={isMaster ? (c) => setCredsClient(c) : null}
              onAssign={canAssign ? (c) => openAssign(c) : null}
              onViewLogs={(c) => setLogsClient(c)}
              onChangeStatus={setLifecycle}
              onCreateFolder={createDocFolder}
              folderBusyId={folderBusyId}
              onEditUdise={openUdiseEdit}
            />
          </TabsContent>
        )}

        {canViewInactiveTab && (
          <TabsContent value="inactive" className="mt-4 space-y-3" data-testid="doc-inactive-tab">
            <LifecycleSubTabs
              tone="slate"
              label="Inactive"
              currentLifecycle="Inactive"
              sub={inactiveSub}
              setSub={setInactiveSub}
              atl1List={inactiveATL1}
              atl2List={inactiveATL2}
              onView={(c) => setDetailClient(c)}
              onCreds={isMaster ? (c) => setCredsClient(c) : null}
              onAssign={canAssign ? (c) => openAssign(c) : null}
              onViewLogs={(c) => setLogsClient(c)}
              onChangeStatus={setLifecycle}
              onCreateFolder={createDocFolder}
              folderBusyId={folderBusyId}
              onEditUdise={openUdiseEdit}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* v109.9.40 — DocTabAccessDialog removed per user request */}

      {/* Doc-team logs viewer (any tab → "Logs" action) */}
      <DocLogsDialog
        client={logsClient}
        onOpenChange={(o) => { if (!o) setLogsClient(null); }}
      />

      <DocStatusDialog
        open={!!active.client}
        readOnly={active.mode === "view"}
        onOpenChange={(o) => { if (!o) setActive({ client: null, mode: null }); }}
        client={active.client}
        onSaved={load}
      />

      <ClientFormDialog
        open={newClientOpen}
        onOpenChange={setNewClientOpen}
        onSaved={() => load()}
        title="New Client (Pending Approval)"
        submitLabel="Submit for Approval"
      />

      <MapExistingDialog open={mapOpen} onOpenChange={setMapOpen} />

      <SchoolCredentialsDialog
        open={!!credsClient}
        onOpenChange={(o) => { if (!o) setCredsClient(null); }}
        client={credsClient}
      />

      {/* Update U-DISE — pushes to the Client Database (canonical source). */}
      <Dialog open={!!udiseEditClient} onOpenChange={(o) => { if (!o) setUdiseEditClient(null); }}>
        <DialogContent className="max-w-md" data-testid="doc-udise-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              <Edit3 size={16} className="text-indigo-600" />
              Update U-DISE
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-[12.5px]">
            <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
              <div className="text-[10.5px] uppercase font-bold tracking-wider text-slate-500">School</div>
              <div className="text-[13px] font-semibold text-slate-900">{udiseEditClient?.client_name}</div>
              <div className="text-[10.5px] mono text-slate-500 mt-0.5">
                Current U-DISE: <b>{udiseEditClient?.u_dise || "—"}</b>
              </div>
            </div>
            <div>
              <label className="text-[10.5px] uppercase font-bold text-slate-600 tracking-wider">New U-DISE</label>
              <Input
                value={udiseNewValue}
                onChange={(e) => setUdiseNewValue(e.target.value.replace(/\D/g, ""))}
                placeholder="e.g. 09030400999"
                inputMode="numeric"
                pattern="\d*"
                maxLength={14}
                className="mt-1 mono"
                data-testid="doc-udise-input"
                autoFocus
              />
              <div className="text-[10.5px] text-slate-500 mt-1">
                8–14 digits. Must be unique across the Client Database.
              </div>
            </div>
            <div className="text-[10.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Changing U-DISE pushes the new value to the Client Database — Orders, Dispatch and Tenders all use the same record, so the change appears everywhere immediately.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setUdiseEditClient(null)}>Cancel</Button>
            <Button
              onClick={saveUdise}
              disabled={udiseSaving || !udiseNewValue}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="doc-udise-save"
            >
              {udiseSaving ? "Saving…" : "Update & Push to DB"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Full Details dialog */}
      <Dialog open={!!detailClient} onOpenChange={(o) => { if (!o) setDetailClient(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="doc-detail-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              {detailClient?.client_name}
              {detailClient?.is_hot && <span className="inline-flex items-center gap-0.5 bg-rose-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full"><Flame size={10} /> HOT</span>}
            </DialogTitle>
            <div className="text-[11px] mono text-slate-500">U-DISE · {detailClient?.u_dise || "—"}</div>
          </DialogHeader>
          {detailClient && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <DInfo label="Client Type" value={detailClient.client_type} />
                <DInfo label="Client Email" value={detailClient.client_email} />
                <DInfo label="ATL Code" value={detailClient.atl_code} />
                <DInfo label="Sales Owner" value={detailClient.assigned_to_name} />
                <DInfo label="Doc Team" value={(detailClient.documentation_assignee_names || []).join(", ")} full />
                <DInfo label="Address" value={detailClient.address} full />
                <DInfo label="District" value={detailClient.district} />
                <DInfo label="State" value={detailClient.state} />
                <DInfo label="Pin Code" value={detailClient.pin_code} />
                <DInfo label="WhatsApp Group" value={detailClient.has_whatsapp_group ? `Yes — ${detailClient.whatsapp_group_name || ""}` : (detailClient.whatsapp_no_reason || "No")} full />
                {detailClient.hot_reason && <DInfo label="Hot Reason" value={detailClient.hot_reason} full />}
                {(detailClient.client_type === "ATL 1.0" || (detailClient.client_types || []).includes("ATL 1.0")) && (
                  <>
                    <DInfo label="ATL 2nd Tranche" value={detailClient.atl_2nd_tranche} />
                    <DInfo label="ATL 3rd Tranche" value={detailClient.atl_3rd_tranche} />
                    <DInfo label="Drive Folder?" value={detailClient.atl_has_drive} />
                    {detailClient.atl_drive_link && <DInfo label="Drive Link" value={<a className="text-[var(--pk-accent)] underline" href={detailClient.atl_drive_link} target="_blank" rel="noreferrer">Open</a>} />}
                    <DInfo label="School Funds (INR)" value={detailClient.atl_school_funds} />
                  </>
                )}
                {(detailClient.client_type === "ATL 2.0" || (detailClient.client_types || []).includes("ATL 2.0")) && (
                  <>
                    <DInfo label="Kit Required" value={detailClient.kit_required ? "Yes" : "No"} />
                    <DInfo label="MOU Signed" value={detailClient.mou_signed ? "Yes" : (detailClient.mou_required ? "No (Required)" : "Not Required")} />
                    {detailClient.kit_order_no && <DInfo label="Kit Order #" value={detailClient.kit_order_no} />}
                  </>
                )}
              </div>
              {Array.isArray(detailClient.contacts) && detailClient.contacts.length > 0 && (
                <div>
                  <div className="pk-table-header mb-2">Contacts ({detailClient.contacts.length})</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {detailClient.contacts.map((c, i) => (
                      <div key={i} className="border border-slate-200 rounded-md p-3 text-[12px]" data-testid={`doc-detail-contact-${i}`}>
                        <div className="font-semibold text-slate-900">{c.name || "—"} <span className="text-[10px] text-slate-500 font-normal">· {c.type || "—"}</span></div>
                        {c.email && <div className="mono text-slate-600 truncate">{c.email}</div>}
                        {c.phone && <div className="mono text-slate-600">{c.phone}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailClient(null)} data-testid="doc-detail-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Doc Team dialog */}
      <Dialog open={!!assignTarget} onOpenChange={(o) => { if (!o) { setAssignTarget(null); setSelectedAssignees([]); setAssignSearch(""); } }}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="doc-assign-dialog">
          <DialogHeader>
            <DialogTitle className="heading">Assign Doc Team</DialogTitle>
            <div className="text-[11px] text-slate-500 mt-1">
              {assignTarget?.client_name} · U-DISE {assignTarget?.u_dise || "—"}
            </div>
          </DialogHeader>
          <div className="text-[11px] bg-violet-50 border border-violet-200 rounded-md px-3 py-2 my-2 text-violet-800">
            <strong>Heads-up:</strong> Selected users are automatically granted the
            Documentation module at <strong>Edit</strong> access so they can start
            working immediately. Use Manage Access if you want to upgrade them to Write later.
          </div>
          <Input
            value={assignSearch}
            onChange={(e) => setAssignSearch(e.target.value)}
            placeholder="Search users by name / email / role…"
            className="h-9 text-xs mb-2"
            data-testid="doc-assign-search"
          />
          <div className="py-2 space-y-1 max-h-[50vh] overflow-y-auto">
            {(() => {
              const q = assignSearch.trim().toLowerCase();
              const filtered = docEligible.filter((u) => !q || [u.name, u.email, u.role, u.department, u.designation].some((v) => String(v || "").toLowerCase().includes(q)));
              if (filtered.length === 0) return <div className="text-xs text-slate-400 italic py-4 text-center">No matching users.</div>;
              return filtered.map((u) => (
                <label
                  key={u.id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md border-2 cursor-pointer transition-all ${selectedAssignees.includes(u.id) ? "bg-violet-50 border-violet-400" : "bg-white border-slate-200 hover:border-slate-300"}`}
                  data-testid={`doc-assign-pick-${u.id}`}
                >
                  <Checkbox
                    checked={selectedAssignees.includes(u.id)}
                    onCheckedChange={() => toggleAssignee(u.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{u.name}</div>
                    <div className="text-[11px] text-slate-500">{u.role}{u.department ? ` · ${u.department}` : ""}{u.employee_code ? ` · ${u.employee_code}` : ""}</div>
                  </div>
                </label>
              ));
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAssignTarget(null); setSelectedAssignees([]); }}>Cancel</Button>
            <Button onClick={submitAssign} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="doc-assign-submit">
              Save Assignment ({selectedAssignees.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DInfo({ label, value, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5 break-words">{value || <span className="text-slate-300">—</span>}</div>
    </div>
  );
}

function tonefor(stage) {
  if (stage === "Stage 1 — Drafting" || stage === "Stage 1 — Google Form") return "violet";
  if (stage === "Stage 2 — Uploading" || stage === "Stage 2 — Portal Data") return "blue";
  if (stage === "Stage 3 — Review" || stage === "Stage 3 — Application Status") return "amber";
  if (stage === "Stage 4 — Funded") return "emerald";
  return "slate";
}

function lifecycleChipActive(lc) {
  if (lc === "Working") return "bg-blue-600 border-blue-600 text-white";
  if (lc === "Completed") return "bg-emerald-600 border-emerald-600 text-white";
  if (lc === "Inactive") return "bg-rose-600 border-rose-600 text-white";
  if (lc === "Pending") return "bg-amber-500 border-amber-500 text-white";
  if (lc === "Unassigned") return "bg-amber-500 border-amber-500 text-white";
  return "bg-violet-600 border-violet-600 text-white";
}

function StatCard({ label, value, tone = "slate" }) {
  const tones = {
    violet: "bg-violet-50 border-violet-200 text-violet-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    orange: "bg-orange-50 border-orange-200 text-orange-700",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  };
  return (
    <div className={`border-2 rounded-md p-3 ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70 truncate">{label}</div>
      <div className="text-2xl font-extrabold mt-0.5">{value}</div>
    </div>
  );
}



/* ============================================================================
 * Sub-tabs (ATL 1.0 / ATL 2.0) inside the Completed / Inactive parent tabs.
 * Renders a slim table of clients with a Details (+ optional Creds) action.
 * ==========================================================================*/
function LifecycleSubTabs({ tone, label, currentLifecycle, sub, setSub, atl1List, atl2List, onView, onCreds, onAssign, onViewLogs, onChangeStatus, onCreateFolder, folderBusyId, onEditUdise }) {
  const [viewMode] = useGlobalViewMode();
  // Filter chips: All / ATL 1.0 / ATL 2.0.
  const rows = sub === "atl1" ? atl1List : sub === "atl2" ? atl2List : [...atl1List, ...atl2List];
  const chips = [
    { key: "all", label: `All (${atl1List.length + atl2List.length})` },
    { key: "atl1", label: `ATL 1.0 (${atl1List.length})` },
    { key: "atl2", label: `ATL 2.0 (${atl2List.length})` },
  ];
  const activeClass =
    tone === "amber" ? "bg-amber-500 border-amber-500 text-white"
    : tone === "emerald" ? "bg-emerald-600 border-emerald-600 text-white"
    : tone === "slate" ? "bg-slate-700 border-slate-700 text-white"
    : "bg-violet-600 border-violet-600 text-white";
  // Status options the row can be flipped to (exclude the current lifecycle).
  const statusOptions = ["Working", "Completed", "Inactive"].filter((s) => s !== currentLifecycle);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Filter</span>
        {chips.map((c) => (
          <button
            key={c.key}
            onClick={() => setSub(c.key)}
            className={`h-8 px-3 text-[11.5px] font-semibold rounded-full border transition-all ${sub === c.key ? activeClass : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`}
            data-testid={`doc-${label.toLowerCase()}-filter-${c.key}`}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div className={viewMode === "card" ? "hidden" : "pk-card overflow-x-auto"}>
        <table className="w-full text-sm responsive-table">
          <thead className="border-b border-slate-200 bg-slate-50/60">
            <tr>
              <th className="px-4 py-3 text-left pk-table-header">Client / School</th>
              <th className="px-4 py-3 text-left pk-table-header">U-DISE</th>
              <th className="px-4 py-3 text-left pk-table-header">Type</th>
              <th className="px-4 py-3 text-left pk-table-header">Sales Owner</th>
              <th className="px-4 py-3 text-left pk-table-header">Doc Team</th>
              <th className="px-4 py-3 text-left pk-table-header">Drive Folder</th>
              <th className="px-4 py-3 text-left pk-table-header">Last Updated</th>
              <th className="px-4 py-3 text-right pk-table-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-slate-400 text-sm">No {label.toLowerCase()} clients here.</td></tr>
            )}
            {rows.map((c) => {
              const typeLabel = (c.client_types || []).includes("ATL 2.0") || c.client_type === "ATL 2.0" ? "ATL 2.0" : "ATL 1.0";
              return (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`doc-${label.toLowerCase()}-row-${c.id}`}>
                  <td className="px-4 py-3 font-semibold text-slate-900" data-label="Client">{c.client_name}</td>
                  <td className="px-4 py-3 mono text-[12px]" data-label="U-DISE">
                    <div className="inline-flex items-center gap-1.5">
                      <span>{c.u_dise || "—"}</span>
                      <button
                        type="button"
                        onClick={() => onEditUdise && onEditUdise(c)}
                        title="Edit U-DISE (updates Client Database)"
                        className="text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded p-0.5 transition-colors"
                        data-testid={`doc-${label.toLowerCase()}-edit-udise-${c.id}`}
                      >
                        <Edit3 size={12} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-label="Type">
                    <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px]">{typeLabel}</Badge>
                  </td>
                  <td className="px-4 py-3 text-[12px]" data-label="Sales Owner">{c.assigned_to_name || <span className="text-slate-400 italic">— Unassigned</span>}</td>
                  <td className="px-4 py-3 text-[11px] text-slate-700" data-label="Doc Team">{(c.documentation_assignee_names || []).join(", ") || <span className="text-slate-400 italic">—</span>}</td>
                  <td className="px-4 py-3" data-label="Drive Folder">
                    {c.doc_drive_folder_url ? (
                      <a href={c.doc_drive_folder_url} target="_blank" rel="noreferrer" title={c.doc_drive_folder_name || "Open Drive folder"} className="inline-flex items-center gap-1 text-emerald-700 hover:underline text-[11.5px] font-semibold" data-testid={`doc-${label.toLowerCase()}-drive-link-${c.id}`}>
                        <ExternalLink size={11} /> Open
                      </a>
                    ) : (
                      <Button onClick={() => onCreateFolder && onCreateFolder(c)} size="sm" variant="outline" disabled={!onCreateFolder || folderBusyId === c.id} className="h-7 px-2 text-[10.5px] bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100" data-testid={`doc-${label.toLowerCase()}-drive-create-${c.id}`}>
                        <FolderPlus size={10} className="mr-1" />
                        {folderBusyId === c.id ? "Creating…" : "Create"}
                      </Button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[11px] mono text-slate-600" data-label="Last Updated">{c.doc_lifecycle_updated_at ? new Date(c.doc_lifecycle_updated_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</td>
                  <td className="px-4 py-3 text-right" data-label="Actions">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <Button size="sm" variant="outline" className="h-8 px-2.5 text-[11px]" onClick={() => onView(c)} data-testid={`doc-${label.toLowerCase()}-view-${c.id}`}>
                        <Eye size={11} className="mr-1" /> Details
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 px-2.5 text-[11px]" onClick={() => onViewLogs(c)} data-testid={`doc-${label.toLowerCase()}-logs-${c.id}`}>
                        <FileText size={11} className="mr-1" /> Logs
                      </Button>
                      {onAssign && (
                        <Button size="sm" variant="outline" className="h-8 px-2.5 text-[11px] bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100" onClick={() => onAssign(c)} data-testid={`doc-${label.toLowerCase()}-assign-${c.id}`}>
                          <UserPlus size={11} className="mr-1" /> Assign
                        </Button>
                      )}
                      {onCreds && (
                        <Button size="sm" variant="outline" className="h-8 px-2.5 text-[11px]" onClick={() => onCreds(c)} data-testid={`doc-${label.toLowerCase()}-creds-${c.id}`}>
                          <KeyRound size={11} className="mr-1" /> Creds
                        </Button>
                      )}
                      <select
                        defaultValue=""
                        onChange={(e) => { const v = e.target.value; if (v) { onChangeStatus(c, v); e.target.value = ""; } }}
                        className="h-8 text-[11px] border border-slate-200 rounded-md px-2 bg-white dark:bg-slate-900 dark:border-slate-700"
                        data-testid={`doc-${label.toLowerCase()}-status-${c.id}`}
                      >
                        <option value="">Change status…</option>
                        {statusOptions.map((s) => (
                          <option key={s} value={s}>Mark as {s}</option>
                        ))}
                      </select>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {viewMode === "card" && (
        rows.length === 0 ? (
          <div className="pk-card p-12 text-center text-slate-400 text-sm">Nothing here yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid={`doc-${label.toLowerCase()}-cards`}>
            {rows.map((c) => {
              const isATL2 = (c.client_type === "ATL 2.0") || (c.client_types || []).includes("ATL 2.0");
              const typeLabel = isATL2 ? "ATL 2.0" : "ATL 1.0";
              const docTeam = (c.documentation_assignee_names || []).join(", ");
              return (
                <div key={c.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`doc-${label.toLowerCase()}-card-${c.u_dise || c.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-slate-900 dark:text-slate-100 truncate">{c.client_name}</div>
                      <div className="mono text-[10.5px] text-slate-500 dark:text-slate-400 truncate flex items-center gap-1.5">
                        <span>U-DISE {c.u_dise || "—"}</span>
                        {onEditUdise && (
                          <button
                            type="button"
                            onClick={() => onEditUdise(c)}
                            title="Edit U-DISE (updates Client Database)"
                            className="text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded p-0.5 transition-colors"
                            data-testid={`doc-${label.toLowerCase()}-edit-udise-card-${c.id}`}
                          >
                            <Edit3 size={10} />
                          </button>
                        )}
                      </div>
                    </div>
                    <Badge className={`text-[10px] shrink-0 ${isATL2 ? "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300" : "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"}`}>{typeLabel}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                    <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">Sales Owner</div><div className="text-slate-800 dark:text-slate-200 truncate">{c.assigned_to_name || <span className="text-slate-400 italic">—</span>}</div></div>
                    <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">Doc Team</div><div className="text-slate-800 dark:text-slate-200 truncate">{docTeam || <span className="text-slate-400 italic">—</span>}</div></div>
                    <div className="col-span-2">
                      <div className="text-[9.5px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">Drive Folder</div>
                      {c.doc_drive_folder_url ? (
                        <a
                          href={c.doc_drive_folder_url}
                          target="_blank"
                          rel="noreferrer"
                          title={c.doc_drive_folder_name || "Open Drive folder"}
                          className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 hover:underline text-[11.5px] font-semibold"
                          data-testid={`doc-${label.toLowerCase()}-drive-link-card-${c.id}`}
                        >
                          <ExternalLink size={11} /> {c.doc_drive_folder_name || "Open"}
                        </a>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-[10.5px] bg-emerald-50 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-100"
                          onClick={() => onCreateFolder && onCreateFolder(c)}
                          disabled={!onCreateFolder || folderBusyId === c.id}
                          data-testid={`doc-${label.toLowerCase()}-drive-create-card-${c.id}`}
                        >
                          <FolderPlus size={10} className="mr-1" />
                          {folderBusyId === c.id ? "Creating…" : "Create"}
                        </Button>
                      )}
                    </div>
                    <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">Last Updated</div><div className="mono text-slate-600 dark:text-slate-300 text-[10.5px]">{c.doc_lifecycle_updated_at ? new Date(c.doc_lifecycle_updated_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</div></div>
                  </div>
                  <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex flex-wrap gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px]" onClick={() => onView(c)} data-testid={`doc-${label.toLowerCase()}-details-card-${c.id}`}>
                      <Eye size={10} className="mr-1" /> Details
                    </Button>
                    {onViewLogs && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px] text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/30" onClick={() => onViewLogs(c)} data-testid={`doc-${label.toLowerCase()}-logs-card-${c.id}`}>
                        <FileText size={10} className="mr-1" /> Logs
                      </Button>
                    )}
                    {onAssign && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px] bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 hover:bg-amber-100" onClick={() => onAssign(c)} data-testid={`doc-${label.toLowerCase()}-assign-card-${c.id}`}>
                        <UserPlus size={10} className="mr-1" /> Assign
                      </Button>
                    )}
                    {onCreds && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px]" onClick={() => onCreds(c)} data-testid={`doc-${label.toLowerCase()}-creds-card-${c.id}`}>
                        <KeyRound size={10} className="mr-1" /> Creds
                      </Button>
                    )}
                    <select
                      defaultValue=""
                      onChange={(e) => { const v = e.target.value; if (v) { onChangeStatus(c, v); e.target.value = ""; } }}
                      className="h-7 text-[10.5px] border border-slate-200 dark:border-slate-700 rounded-md px-1.5 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
                      data-testid={`doc-${label.toLowerCase()}-status-card-${c.id}`}
                    >
                      <option value="">Change status…</option>
                      {statusOptions.map((s) => (<option key={s} value={s}>Mark as {s}</option>))}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

/* ============================================================================
 * Documentation team logs viewer — shows the full audit trail for a single
 * client (lifecycle changes, assignments, manual remarks, stage diffs).
 * ==========================================================================*/
function DocLogsDialog({ client, onOpenChange }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!client) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/crm/clients/${client.id}/doc-logs`);
        setLogs(data || []);
      } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, [client]);
  return (
    <Dialog open={!!client} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="doc-logs-dialog">
        <DialogHeader>
          <DialogTitle className="heading">Documentation Team Logs</DialogTitle>
          <div className="text-[11px] text-slate-500 mt-1">{client?.client_name} · U-DISE {client?.u_dise || "—"}</div>
        </DialogHeader>
        {loading && <div className="py-6 text-center text-xs text-slate-400">Loading…</div>}
        {!loading && logs.length === 0 && (
          <div className="py-6 text-center text-xs text-slate-400 italic">No documentation activity yet.</div>
        )}
        <div className="space-y-2 mt-2">
          {logs.map((l) => (
            <div key={l.id || l._id} className="rounded-md border border-slate-200 bg-white px-3 py-2.5 text-[12px]" data-testid={`doc-log-${l.id || l._id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-semibold text-slate-800">{l.created_by_name || "—"}</div>
                <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px]">{(l.log_type || "log").replaceAll("_", " ")}</Badge>
              </div>
              <div className="text-[10.5px] text-slate-500 mono mt-0.5">
                {l.created_at ? new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}
              </div>
              {(l.changes || []).map((ch, i) => (
                <div key={i} className="mt-1 text-[11.5px]">
                  <span className="text-slate-500">{ch.field}:</span>{" "}
                  <span className="text-rose-600 line-through">{Array.isArray(ch.from) ? ch.from.join(", ") || "—" : (ch.from || "—")}</span>{" → "}
                  <span className="text-emerald-700 font-semibold">{Array.isArray(ch.to) ? ch.to.join(", ") || "—" : (ch.to || "—")}</span>
                </div>
              ))}
              {l.remark && <div className="text-[12px] text-slate-700 mt-1.5 whitespace-pre-wrap">{l.remark}</div>}
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================================
 * Master-only Tab Access dialog. Lets the master grant per-user access to the
 * Documentation Unassigned / Working / Completed / Inactive tabs.
 * ==========================================================================*/
function DocTabAccessDialog({ open, onOpenChange, users }) {
  const [grants, setGrants] = useState({}); // { user_id: { unassigned, completed, inactive } }
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/documentation/tab-access");
        const map = {};
        (data || []).forEach((g) => {
          map[g.user_id] = {
            unassigned: (g.tabs || []).includes("unassigned"),
            working: (g.tabs || []).includes("working"),
            completed: (g.tabs || []).includes("completed"),
            inactive: (g.tabs || []).includes("inactive"),
          };
        });
        setGrants(map);
      } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    })();
  }, [open]);

  const toggle = (uid, tab) => {
    setGrants((g) => ({
      ...g,
      [uid]: { ...(g[uid] || {}), [tab]: !((g[uid] || {})[tab]) },
    }));
  };

  const save = async (uid) => {
    setBusy(true);
    try {
      const cur = grants[uid] || {};
      const tabs = [];
      if (cur.unassigned) tabs.push("unassigned");
      if (cur.working) tabs.push("working");
      if (cur.completed) tabs.push("completed");
      if (cur.inactive) tabs.push("inactive");
      await api.post("/documentation/tab-access", { user_id: uid, tabs });
      toast.success("Saved");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const filtered = (users || []).filter((u) =>
    !search.trim() || [u.name, u.email, u.role, u.department].some((v) => String(v || "").toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="doc-tab-access-dialog">
        <DialogHeader><DialogTitle className="heading">Documentation · Tab Access</DialogTitle></DialogHeader>
        <p className="text-[11.5px] text-slate-600 mb-2">
          Choose which extra tabs (Unassigned / Working / Completed / Inactive) each user can view. Master Admin and Documentation Admin always see every tab.
        </p>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search user…" className="h-9 text-xs mb-3" data-testid="doc-tab-access-search" />
        <table className="w-full text-xs">
          <thead className="bg-slate-50/60 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left">User</th>
              <th className="px-3 py-2 text-center">Unassigned</th>
              <th className="px-3 py-2 text-center">Working</th>
              <th className="px-3 py-2 text-center">Completed</th>
              <th className="px-3 py-2 text-center">Inactive</th>
              <th className="px-3 py-2 text-right">—</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (<tr><td colSpan={6} className="text-center text-slate-400 py-6">No users.</td></tr>)}
            {filtered.map((u) => {
              const g = grants[u.id] || {};
              return (
                <tr key={u.id} className="border-t" data-testid={`doc-tab-access-row-${u.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-slate-800">{u.name}</div>
                    <div className="text-[10px] text-slate-500">{u.email}</div>
                  </td>
                  <td className="px-3 py-2 text-center"><input type="checkbox" checked={!!g.unassigned} onChange={() => toggle(u.id, "unassigned")} data-testid={`doc-tab-access-unassigned-${u.id}`} /></td>
                  <td className="px-3 py-2 text-center"><input type="checkbox" checked={!!g.working} onChange={() => toggle(u.id, "working")} data-testid={`doc-tab-access-working-${u.id}`} /></td>
                  <td className="px-3 py-2 text-center"><input type="checkbox" checked={!!g.completed} onChange={() => toggle(u.id, "completed")} data-testid={`doc-tab-access-completed-${u.id}`} /></td>
                  <td className="px-3 py-2 text-center"><input type="checkbox" checked={!!g.inactive} onChange={() => toggle(u.id, "inactive")} data-testid={`doc-tab-access-inactive-${u.id}`} /></td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" className="h-7 text-[10.5px]" disabled={busy} onClick={() => save(u.id)} data-testid={`doc-tab-access-save-${u.id}`}>Save</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
